import * as R from 'ramda'
import { browserLanguage } from 'utils/translate.js'

// Actions
export const RESET_PASSWORD_CONFIRM = 'auth/RESET_PASSWORD_CONFIRM'
export const resetPasswordConfirm = ({ password, token, uid }) => ({
  type: RESET_PASSWORD_CONFIRM,
  payload: { password, token, uid },
})

export const RESET_PASSWORD_CONFIRM_FAILED =
  'auth/RESET_PASSWORD_CONFIRM_FAILED'
export const resetPasswordConfirmFailed = error => ({
  type: RESET_PASSWORD_CONFIRM_FAILED,
  error,
})

export const RESET_PASSWORD = 'auth/RESET_PASSWORD'
export const RESET_PASSWORD_FAILED = 'auth/RESET_PASSWORD_FAILED'
export const resetPassword = email => ({
  type: RESET_PASSWORD,
  payload: { email },
})
export const resetPasswordFailed = error => ({
  type: RESET_PASSWORD_FAILED,
  error,
})

export const LOG_IN = 'auth/LOG_IN'
export const LOG_IN_SUCCESS = 'auth/LOG_IN_SUCCESS'
export const LOG_IN_FAILED = 'auth/LOG_IN_FAILED'
export const LOG_OUT = 'auth/LOG_OUT'
export const logIn = (email, password) => ({
  type: LOG_IN,
  payload: { email, password },
})
export const loginSuccess = ({ key }) => ({
  type: LOG_IN_SUCCESS,
  payload: { key },
})
export const loginFailed = error => ({ type: LOG_IN_FAILED, error })
export const logOut = () => ({ type: LOG_OUT, payload: {} })

export const SWITCH_ACCOUNT = 'auth/SWITCH_ACCOUNT'
export const switchAccount = account => ({
  type: SWITCH_ACCOUNT,
  payload: account,
})

export const REGISTER_ACCOUNT = 'auth/REGISTER_ACCOUNT'
export const REGISTER_ACCOUNT_SUCCESS = 'auth/REGISTER_ACCOUNT_SUCCESS'
export const REGISTER_ACCOUNT_FAILED = 'auth/REGISTER_ACCOUNT_FAILED'
export const registerAccount = data => ({
  type: REGISTER_ACCOUNT,
  payload: data,
})
export const registerAccountSuccess = () => ({
  type: REGISTER_ACCOUNT_SUCCESS,
})
export const registerAccountFailed = error => ({
  type: REGISTER_ACCOUNT_FAILED,
  error,
})

export const REQUEST_USER = 'auth/REQUEST_USER'
export const REQUEST_USER_SUCCESS = 'auth/REQUEST_USER_SUCCESS'
export const REQUEST_USER_FAILED = 'auth/REQUEST_USER_FAILED'
export const requestUser = () => ({ type: REQUEST_USER, payload: {} })
export const requestUserSuccess = data => ({
  type: REQUEST_USER_SUCCESS,
  payload: data,
})
export const requestUserFailed = error => ({ type: REQUEST_USER_FAILED, error })

export const SET_LANGUAGE = 'auth/SET_LANGUAGE'
export const setLanguage = lc => ({ type: SET_LANGUAGE, payload: lc })

// User athentication authState enumerator
export const AUTHSTATUS = {
  UNKNOWN: 'unknown',
  PENDING: 'pending',
  LOGGED_IN: 'logged in',
  LOGGED_OUT: 'logged out',
  ERROR: 'error',
}

// Lenses
// lens :: string -> Lens
const lens = R.pipe(
  R.split('.'),
  R.lensPath,
)
const authSlice = lens('auth')
const authTokenLens = lens('key')
const languageLens = lens('language')
const accountsLens = lens('accounts')
const activeAccountLens = lens('active_account')
const authStateLens = lens('authState')
const errorLens = lens('error')

// Selectors
// selectorFromLens :: Lens -> State -> Any
const selectorFromLens = R.pipe(
  R.curryN(2, R.compose)(authSlice),
  R.view,
)

export const getUser = state => R.view(authSlice, state) || {}
export const getAccounts = selectorFromLens(accountsLens)
const getActiveAccount = selectorFromLens(activeAccountLens)

export const getCurrentAccount = R.converge(
  (account_id, accounts) => R.find(R.propEq('id', account_id), accounts),
  [getActiveAccount, getAccounts],
)
export const getLanguage = selectorFromLens(languageLens)

// django rest framework authentication token
export const getAuthToken = selectorFromLens(authTokenLens)

// feature flags for current user
export const getFeatureFlags = R.pipe(
  getCurrentAccount,
  R.propOr({}, 'featureFlags'),
)

// django orm permissions for current user
export const getPermission = R.pipe(
  R.split(/[^a-z]/g),
  R.reverse,
  R.prepend('permissions'),
  R.lensPath,
  l => s => Boolean(selectorFromLens(l)(s)),
)

// reducers
const initialState = {
  language: browserLanguage(),
  authState: AUTHSTATUS.UNKNOWN,
  accounts: [],
  active_account: null,
}

const getReducer = ({ type, payload, error }) => {
  switch (type) {
    case LOG_IN:
    case REQUEST_USER:
      return R.set(authStateLens, AUTHSTATUS.PENDING)
    case REQUEST_USER_SUCCESS:
      return R.compose(
        R.mergeLeft(payload),
        R.set(errorLens, null),
        R.set(authStateLens, AUTHSTATUS.LOGGED_IN),
      )
    case LOG_IN_SUCCESS:
      return R.compose(
        R.set(authTokenLens, payload.key),
        R.set(authStateLens, AUTHSTATUS.LOGGED_IN),
      )
    case LOG_IN_FAILED:
      return R.compose(
        R.set(authStateLens, AUTHSTATUS.ERROR),
        R.set(errorLens, error),
      )
    case REQUEST_USER_FAILED:
      return R.compose(R.set(authStateLens, AUTHSTATUS.LOGGED_OUT))
    case LOG_OUT:
      return R.always(R.set(authStateLens, AUTHSTATUS.LOGGED_OUT)(initialState))
    case SET_LANGUAGE:
      return R.set(languageLens, payload)
    case RESET_PASSWORD_CONFIRM_FAILED:
      return R.set(errorLens, error)
    case SWITCH_ACCOUNT:
      return R.over(
        accountsLens,
        R.map(
          R.converge(R.assoc('current'), [R.propEq('id', payload), R.identity]),
        ),
      )
    default:
      return R.identity
  }
}

export const reducer = (state = initialState, action = {}) =>
  getReducer(action)(state)
